import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { X, Plus, Trash2 } from "lucide-react";
import { Offcanvas } from "react-bootstrap";
import { addCutLot } from "../../../../../Store/Store/Transformation/lot/addCutLot";
import { useDispatch, useSelector } from "react-redux";
import { getOneLot } from "../../../../../Store/Store/Transformation/lot/getOneLot";
import { updateCutLot } from "../../../../../Store/Store/Transformation/lot/updateCutLot";
import Swal from "sweetalert2";
import { V_URL } from "../../../../../BaseUrl";
import axios from "axios";
import { lotStatus } from "../../../../../helper/StatusFile";
import { getLotList } from "../../../../../Store/Store/Transformation/lot/getLotList";

const emptyPiece = () => ({
    length: "",
    width: "",
    weight: "",
    code: "",
    wastage: {
        length: "",
        width: "",
        area: "",
        weight: "",
        reason: "",
        type: "",
    }
});

const CutPieceDrawer = ({ open, onClose, item, onSaved }) => {
    const [pieces, setPieces] = useState([]);
    const [errors, setErrors] = useState([]);
    const [editing, setEditing] = useState(null);

    const dispatch = useDispatch()

    const columns = [
        { key: "code", label: "Piece Code", parent: null, width: "100px", placeholder: "code" },
        { key: "length", label: "Length", parent: null, width: "80px", placeholder: "length" },
        { key: "width", label: "Width", parent: null, width: "80px", placeholder: "width" },
        { key: "weight", label: "Weight", parent: null, width: "80px", placeholder: "weight" },

        { key: "length", label: "Wastage Length", parent: "wastage", width: "100px", placeholder: "length" },
        { key: "width", label: "Wastage Width", parent: "wastage", width: "100px", placeholder: "width" },
        { key: "area", label: "Wastage Area", parent: "wastage", width: "100px", placeholder: "area" },
        { key: "weight", label: "Wastage Weight", parent: "wastage", width: "100px", placeholder: "weight" },
        { key: "reason", label: "Wastage Reason", parent: "wastage", width: "300px", placeholder: "reason" },
        { key: "type", label: "Wastage Type", parent: "wastage", width: "150px", placeholder: "type" },
    ];
    const [active, setActive] = useState({ row: 0, col: 0 });
    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            setEditing(null);
            return;
        }

        if (editing) return;

        if (!pieces.length) return;

        const isPrintable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
        const isBackspace = e.key === "Backspace";
        const isDelete = e.key === "Delete";

        if (isPrintable || isBackspace || isDelete) {
            const { row, col } = active;
            if (columns[col]) {
                const colDef = columns[col];
                const key = colDef.key;
                const parent = colDef.parent;

                if (isDelete) {
                    updatePiece(row, key, "", parent);
                    return;
                }

                const isTextField = ["code", "reason", "type"].includes(key);
                if (!isTextField && isPrintable && !/[\d.-]/.test(e.key)) {
                    return;
                }

                e.preventDefault();
                setEditing({ row, col });
                updatePiece(row, key, isBackspace ? "" : e.key, parent);
                setTimeout(() => {
                    document.getElementById(`cell-${row}-${col}`)?.focus();
                });
                return;
            }
        }

        let { row, col } = active;

        if (e.key === "ArrowRight") col = Math.min(col + 1, columns.length - 1);
        if (e.key === "ArrowLeft") col = Math.max(col - 1, 0);
        if (e.key === "ArrowDown") row = Math.min(row + 1, pieces.length - 1);
        if (e.key === "ArrowUp") row = Math.max(row - 1, 0);

        if (e.key === "Enter") {
            e.preventDefault();
            setEditing({ row, col });
            setTimeout(() => {
                document.getElementById(`cell-${row}-${col}`)?.focus();
            });
            return;
        }

        setActive({ row, col });

        const nextInput = document.getElementById(`cell-${row}-${col}`);
        if (nextInput) nextInput.focus();
    };

    useEffect(() => {
        if (item?._id) {
            dispatch(getOneLot({
                lot_id: item?._id,
            }));
        }
    }, [item, dispatch]);

    const lotDetails = useSelector((state) => state.getOneLot.user?.data);

    const isMaterialFullyUsed = useMemo(() => {
        if (!item) return false;

        switch (item.stock_uom_type) {
            case "AREA":
                return (item.remaining_area || 0) <= 0;
            case "LENGTH":
            case "QTY":
                return (item.remaining_qty || 0) <= 0;
            case "WEIGHT":
                return (item.dimensions?.weight || 0) <= 0;
            default:
                return false;
        }
    }, [item]);

    useEffect(() => {
        if (!open || !item) return;

        if (!Array.isArray(lotDetails)) {
            setPieces([emptyPiece()]);
            setErrors([{}]);
            return;
        }

        const currentLotPieces = lotDetails.filter(
            (p) => p.lot_id === item._id
        );

        if (!currentLotPieces.length) {
            setPieces([emptyPiece()]);
            setErrors([{}]);
            return;
        }

        const formattedPieces = currentLotPieces.map((piece) => ({
            _id: piece._id,
            length: piece.dimensions?.length?.toString() || "",
            width: piece.dimensions?.width?.toString() || "",
            weight: piece.dimensions?.weight?.toString() || "",
            code: piece.piece_code || "",
            status: piece.status || "",

            wastage: {
                length: piece.wastageData?.length?.toString() || "",
                width: piece.wastageData?.width?.toString() || "",
                area: piece.wastageData?.area?.toString() || "",
                weight: piece.wastageData?.weight?.toString() || "",
                reason: piece.wastageData?.remarks || "",
                type: piece.wastageData?.wastage_type || "",
            }
        }));

        setPieces(formattedPieces);
        setErrors(formattedPieces.map(() => ({})));

    }, [open, item, lotDetails]);

    const addPiece = () => {
        setPieces((p) => [...p, emptyPiece()]);
        setErrors((e) => [...e, {}]);
    };

    const updatePiece = (rowIndex, key, value, parent) => {
        const copy = [...pieces];

        if (parent) {
            copy[rowIndex][parent][key] = value;

            if (parent === "wastage" && (key === "length" || key === "width")) {
                const length = parseFloat(copy[rowIndex].wastage.length);
                const width = parseFloat(copy[rowIndex].wastage.width);

                if (!isNaN(length) && !isNaN(width)) {
                    copy[rowIndex].wastage.area = (length * width).toFixed(2);
                } else {
                    copy[rowIndex].wastage.area = "";
                }
            }
        } else {
            copy[rowIndex][key] = value;
        }

        setPieces(copy);

        if (!parent && (key === 'length' || key === 'width')) {
            setErrors(prev => {
                const newErrors = [...prev];
                if (newErrors[rowIndex] && newErrors[rowIndex][key]) {
                    const rowErrors = { ...newErrors[rowIndex] };
                    delete rowErrors[key];
                    newErrors[rowIndex] = rowErrors;
                }
                return newErrors;
            });
        }
    };

    const deletePiece = (i) => {
        const copy = [...pieces];
        copy.splice(i, 1);
        setPieces(copy);

        const errorCopy = [...errors];
        errorCopy.splice(i, 1);
        setErrors(errorCopy);
    };

    const validate = () => {
        if (!item) return false;

        const transformType = item.transform_type;
        const stockUomType = item.stock_uom_type;


        const existingPieceMap = {};

        if (Array.isArray(lotDetails)) {
            lotDetails.forEach(lp => {
                existingPieceMap[lp._id] = lp;
            });
        }

        const newErrors = pieces.map((p) => {
            const rowErrors = {};

            if (p.code === 'AUTO-PIECE') {
                return {}; // no errors for this row
            }

            const length = parseFloat(p.length);
            const width = parseFloat(p.width);
            const weight = parseFloat(p.weight);

            switch (transformType) {
                case "PLATE":
                case "SHEET":
                    if (stockUomType === "AREA") {

                        if (!length || length <= 0)
                            rowErrors.length = "Invalid length";

                        if (!width || width <= 0)
                            rowErrors.width = "Invalid width";

                        const newArea = length * width;
                        let allowedArea = item.remaining_area || 0;

                        if (p._id && Array.isArray(lotDetails)) {
                            const oldPiece = lotDetails.find(lp => lp._id === p._id);

                            if (oldPiece) {
                                const oldLength = oldPiece.dimensions?.length || 0;
                                const oldWidth = oldPiece.dimensions?.width || 0;
                                const oldArea = oldLength * oldWidth;

                                allowedArea += oldArea;
                            }
                        }

                        if (newArea > allowedArea) {
                            rowErrors.length = `Exceeds available area (${allowedArea})`;
                            rowErrors.width = `Exceeds available area (${allowedArea})`;
                        }
                    }

                    if (stockUomType === "WEIGHT") {
                        if (!weight || weight <= 0)
                            rowErrors.weight = "Invalid weight";
                        if (weight > item.dimensions.weight)
                            rowErrors.weight = `Exceeds lot weight (${item.dimensions.weight})`;
                    }
                    break;

                case "PIPE":
                case "BAR":
                    if (stockUomType === "QTY") {
                        if (!length || length <= 0)
                            rowErrors.length = "Invalid length";
                    }
                    if (stockUomType === "LENGTH") {
                        if (!length || length <= 0)
                            rowErrors.length = "Invalid length";

                        let allowedLength = item.remaining_qty;

                        if (p._id && existingPieceMap[p._id]) {
                            allowedLength += existingPieceMap[p._id].dimensions.length;
                        }

                        if (length > allowedLength) {
                            rowErrors.length = `Exceeds available length (${allowedLength})`;
                        }

                    }
                    if (stockUomType === "WEIGHT") {
                        if (!weight || weight <= 0)
                            rowErrors.weight = "Invalid weight";
                        if (weight > item.dimensions.weight)
                            rowErrors.weight = `Exceeds lot weight (${item.dimensions.weight})`;
                    }
                    break;

                default:
                    rowErrors.general = `Unsupported transform type`;
            }
            return rowErrors;
        });
        setErrors(newErrors);
        return newErrors.every(err => Object.keys(err).length === 0);
    };

    const handleSave = () => {
        if (!validate()) {
            return;
        }
        const isEditMode = Array.isArray(lotDetails) && lotDetails.length > 0;

        const formattedCuts = pieces.map((p) => {
            const hasWastage =
                p.wastage.length || p.wastage.width || p.wastage.area || p.wastage.weight || p.wastage.type;

            const cutData = {
                piece_id: p._id || null,
                length: Number(p.length),
                width: Number(p.width),
                weight: Number(p.weight),
                code: p.code,
            };

            if (hasWastage) {
                cutData.wastage = {
                    length: Number(p.wastage.length),
                    width: Number(p.wastage.width),
                    area: Number(p.wastage.area),
                    weight: Number(p.wastage.weight),
                    reason: p.wastage.reason,
                    type: p.wastage.type,
                };
            }

            return cutData;
        });

        if (isEditMode) {
            dispatch(updateCutLot({
                lot_id: item._id,
                user_id: localStorage.getItem("PAY_USER_ID"),
                cuts: formattedCuts
            }))
                .then((res) => {
                    if (res.payload.success) {
                        toast.success(res.payload.message);
                        dispatch(getOneLot({ lot_id: item._id }));
                        onSaved();
                        onClose();
                    }
                });

        } else {
            dispatch(addCutLot({
                lot_id: item._id,
                user_id: localStorage.getItem("PAY_USER_ID"),
                cuts: formattedCuts
            }))
                .then((res) => {
                    if (res.payload.success) {
                        toast.success(res.payload.message);
                        dispatch(getOneLot({ lot_id: item._id }));
                        onSaved();
                        onClose();
                    }
                });
        }

    };

    const handleDeletePiece = (id) => {
        Swal.fire({
            title: `Are you sure want to delete?`,
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then((result) => {
            if (result.isConfirmed) {
                const myurl = `${V_URL}/prosafe/delete-piece/${id}`;
                axios({
                    method: "delete",
                    url: myurl,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + localStorage.getItem('PAY_USER_TOKEN')
                    },
                }).then((response) => {
                    if (response.data.success === true) {
                        toast.success(response?.data?.message);
                        dispatch(getOneLot({ lot_id: item._id }));
                    } else {
                        toast.error(response?.data?.message);
                    }
                }).catch((error) => {
                    toast.error("Something went wrong");
                    console?.log("Errors", error);
                });
            }
        });
    }

    // const isAddDisabled = pieces.some(p => !p.length || !p.width || !p.weight);

    return (
        <Offcanvas
            show={open}
            onHide={onClose}
            placement="end"
            style={{ width: '1300px', maxWidth: '100%' }}
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>
                    <div className="fw-bold">{item?.item_name || "Item Details"}</div>
                    {item && (
                        <div className="text-muted small" style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                            {item?.lot_no} • {lotStatus(item?.status).name}
                        </div>
                    )}
                </Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body className="p-3 bg-light overflow-auto" tabIndex={0} onKeyDown={handleKeyDown}>
                {isMaterialFullyUsed && (
                    <div className="alert alert-warning">
                        This material is fully used. No more cuts allowed.
                    </div>
                )}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="fw-semibold">Cut Pieces</div>
                    <button className="btn btn-primary btn-sm" onClick={addPiece} disabled={isMaterialFullyUsed}>
                        <Plus size={16} /> Add Piece
                    </button>
                </div>

                {pieces.length === 0 && (
                    <div className="text-center text-muted py-5 bg-white border rounded">
                        No pieces added
                    </div>
                )}

                {pieces.length > 0 && (
                    <div className="table-responsive border rounded bg-white shadow-sm" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                        <table className="table table-bordered mb-0">
                            <thead className="bg-light sticky-top" style={{ zIndex: 1, top: 0, }}>
                                <tr>
                                    <th className="text-center bg-light py-2 px-1" style={{ width: '50px', fontSize: "13px", textAlign: "left" }}>#</th>
                                    {columns.map(col => (
                                        <th key={`${col.parent || "main"}-${col.key}`} className="bg-light py-2 px-1" style={{ minWidth: col.width, fontSize: "13px", textAlign: col.align }}>{col.label}</th>
                                    ))}
                                    <th className="text-center bg-light py-2 px-1" style={{ width: '50px', fontSize: "13px", textAlign: "center" }} />
                                </tr>
                            </thead>

                            <tbody>
                                {pieces.map((piece, rowIndex) => (
                                    <tr key={rowIndex}>
                                        <td className="text-center align-middle bg-light py-0">{rowIndex + 1}</td>

                                        {columns.map((col, colIndex) => {
                                            const isActive =
                                                active.row === rowIndex && active.col === colIndex;

                                            const error = !col.parent && errors[rowIndex]?.[col.key];

                                            return (
                                                <td
                                                    key={`${col.parent || "main"}-${col.key}`}
                                                    className={`p-0 position-relative text-${col.align} ${isActive ? "active-cell" : ""}`}
                                                    style={{ zIndex: pieces.length - rowIndex, textAlign: col.align }}
                                                    onClick={() => {
                                                        setActive({ row: rowIndex, col: colIndex });
                                                        setEditing(null);
                                                        document.getElementById(`cell-${rowIndex}-${colIndex}`)?.focus();
                                                    }}
                                                    onDoubleClick={() => {
                                                        setActive({ row: rowIndex, col: colIndex });
                                                        setEditing({ row: rowIndex, col: colIndex });

                                                        setTimeout(() => {
                                                            document
                                                                .getElementById(`cell-${rowIndex}-${colIndex}`)
                                                                ?.focus();
                                                        });
                                                    }}
                                                >
                                                    <input
                                                        id={`cell-${rowIndex}-${colIndex}`}
                                                        type={
                                                            col.key === "reason" || col.key === "type" || col.key === "code"
                                                                ? "text"
                                                                : "number"
                                                        }
                                                        className={`form-control border-0 shadow-none rounded-0 ${error ? 'is-invalid' : ''}`}
                                                        style={{ minWidth: "100%", height: "100%", fontSize: "15px", padding: "2px 7px", textAlign: col.align }}
                                                        placeholder={col.placeholder}
                                                        value={
                                                            col.parent
                                                                ? piece[col.parent][col.key]
                                                                : piece[col.key]
                                                        }
                                                        onChange={(e) =>
                                                            updatePiece(rowIndex, col.key, e.target.value, col.parent)
                                                        }
                                                        onFocus={() => {
                                                            setActive({ row: rowIndex, col: colIndex });
                                                            if (editing && (editing.row !== rowIndex || editing.col !== colIndex)) {
                                                                setEditing(null);
                                                            }
                                                        }}
                                                        readOnly={
                                                            !editing ||
                                                            editing.row !== rowIndex ||
                                                            editing.col !== colIndex
                                                        }
                                                        disabled={(col.parent === "wastage" && col.key === "area") || piece.status === "ISSUED"}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                e.stopPropagation();

                                                                if (!editing) {
                                                                    setEditing({ row: rowIndex, col: colIndex });
                                                                } else {
                                                                    const nextRow = Math.min(
                                                                        rowIndex + 1,
                                                                        pieces.length - 1
                                                                    );
                                                                    setActive({ row: nextRow, col: colIndex });
                                                                    if (nextRow !== rowIndex) {
                                                                        setEditing({ row: nextRow, col: colIndex });
                                                                        setTimeout(() => {
                                                                            document
                                                                                .getElementById(`cell-${nextRow}-${colIndex}`)
                                                                                ?.focus();
                                                                        });
                                                                    } else {
                                                                        setEditing(null);
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    {/* {error && (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                zIndex: 10,
                                                                // backgroundColor: '#ffe6e6',
                                                                color: '#d32f2f',
                                                                fontSize: '11px',
                                                                letterSpacing: '0.5px',
                                                                // padding: '2px 8px',
                                                                // border: '1px solid #f8b7b7',
                                                                // borderRadius: '0 0 4px 4px',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {error}
                                                        </div>
                                                    )} */}
                                                </td>
                                            );
                                        })}

                                        <td className="text-center align-middle action-col py-0">
                                            <Trash2
                                                size={16}
                                                className={`text-danger ${piece.status === "ISSUED" ? "opacity-50" : ""}`}
                                                style={{ cursor: piece.status === "ISSUED" ? "not-allowed" : "pointer" }}
                                                onClick={() => {
                                                    if (piece.status === "ISSUED") return;
                                                    const targetPiece = pieces[rowIndex];
                                                    if (targetPiece._id) {
                                                        handleDeletePiece(targetPiece._id);
                                                    } else {
                                                        deletePiece(rowIndex);
                                                    }
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {errors.some(row => Object.keys(row).length > 0) && (
                            <div className="mt-3 p-3 bg-white bg-opacity-10">
                                <div className="fw-semibold text-danger mb-2">
                                    Please fix the following errors:
                                </div>

                                <ul className="mb-0 px-3 small text-danger">
                                    {errors.map((rowError, rowIndex) =>
                                        Object.entries(rowError).map(([field, message], i) => (
                                            <li key={`${rowIndex}-${field}-${i}`}>
                                                <strong>Row {rowIndex + 1}</strong> – {field}: {message}
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </Offcanvas.Body>

            <div className="border-top p-3 d-flex justify-content-end gap-2 bg-white">
                <button onClick={onClose} className="btn btn-secondary">
                    Cancel
                </button>
                <button
                    disabled={!pieces.length}
                    onClick={handleSave}
                    className="btn btn-primary d-flex align-items-center gap-2"
                >
                    Save
                </button>
            </div>
        </Offcanvas>
    );
};

export default CutPieceDrawer;
